import * as React from "react";
import moment from "moment";

import { DataTable } from "src/components/common";
import { Button } from "src/components/buttons";

import ReportAPI from "src/admin/report/ReportAPI";
import {
	setDateColumnCellValue,
	DateFilter,
	dateFilterMethod,
} from "src/components/forms/DateFilter";
import LocationAPI from "src/admin/Organization/Location/LocationAPI";
import CoreAPI from "src/core/CoreAPI";
import PickListContext from "src/context/pickListContext";
import { Tooltip } from "antd";
import GenerateExceleRATEPdf from "src/admin/report/templates/v1/GenerateExceleRATEPdf";
import { Colours } from "src/branding/ImpiricaBrand";
import {
	AssessmentClient,
	DatasetPurposeType,
	DatasetsClient,
	ImpiricaUserClient,
	TripInspectionClient,
} from "src/@types/ImpiricaPlatformApi";
import DatasetAPI from "src/admin/Organization/Datasets/DatasetAPI";
import { toast } from "react-toastify";
import { notifyV1ReportsNotSupported } from "src/components/common/V1ReportsNotSupported";
import Utils from "src/common/utils";
interface IDate {
	start: moment.Moment;
	end: moment.Moment;
}

interface IState {
	filtered: any[];
	coreDateCompleted: IDate | null;
	vitalsDateCompleted: IDate | null;
	reports: AssessmentReportListingDTO[];
	isLoading: boolean;
}
interface IProps {
	api?: any;
}

class ReportList extends React.Component<IProps, IState> {
	public static contextType = PickListContext;

	constructor(props: IProps) {
		super(props);
		this.state = {
			vitalsDateCompleted: null,
			coreDateCompleted: null,
			filtered: [],
			reports: [],
			isLoading: true,
		};
	}

	public componentDidMount() {
		ReportAPI.getAllReports().then((res) =>
			this.setState({ reports: res, isLoading: false })
		);
	}

	public render() {
		const columns = this.getColumns();
		const { reports } = this.state;
		return (
			<div className="container-fluid">
				<DataTable
					data={reports}
					loading={this.state.isLoading}
					columns={columns}
					filtered={this.state.filtered}
					defaultSorted={[
						{
							id: "coreDateCompleted",
							desc: true,
						},
						{ id: "vitalsDateCompleted", desc: true },
					]}
					onFilteredChange={(filtered: any) => this.setState({ filtered })}
					filterable={true}
				/>
			</div>
		);
	}

	private onDateChange = (accessor: string, onChange: any) => (e: any) => {
		this.setState((prev) => ({
			...prev,
			[accessor]: {
				start: moment.utc(e[0]).local(),
				end: moment.utc(e[1]).local(),
			},
		}));
		onChange(e);
	};

	private fetchReportData = async (
		reportId: number,
		existingReportId: number | undefined,
		dataLocked: boolean,
		productId: ProductType,
		datasetId: number
	) => {
		let reportFetched = false;

		const clientSiteReportInfo = await ReportAPI.getReport({ reportId });
		if (!clientSiteReportInfo) {
			toast.error("Failed to get client report info");
			return;
		}

		// Only show V2 report if a vitals mobile exists
		if (productId === "ExceleRATEV2" || productId === "DriveABLE") {
			await this.fetchV2ReportData(
				reportId,
				existingReportId,
				dataLocked,
				clientSiteReportInfo,
				productId,
				datasetId
			);
			reportFetched = true;
		} else {
			await this.fetchV1ReportData();
			reportFetched = true;
		}

		return reportFetched;
	};

	private fetchV2ReportData = async (
		reportId: number,
		existingReportId: number | undefined,
		dataLocked: boolean,
		clientSiteReportInfo: ReportNecessaryInfoDTO,
		productId: ProductType,
		datasetId: number
	) => {
		let reportFetched = false;
		const clientId = clientSiteReportInfo.clientId;

		if (dataLocked && existingReportId && existingReportId !== 0) {
			ReportAPI.generateV2ReportPDF({
				reportId,
			});
			reportFetched = true;
		} else {
			let clientSiteGetCoreTestForReport = {};
			let clientSiteGetCareTestForReport = {};
			let clientSiteClientCoreErrorEntries = [{}];

			const clientSiteReportSettingsInfo = await ReportAPI.getReportSettings({
				reportId,
			});
			if (!clientSiteReportSettingsInfo) {
				toast.error("Failed to get client report info settings");
				return;
			}

			const clientSiteOrganizationLocations =
				await LocationAPI.getOrganizationLocations(
					clientSiteReportInfo.organizationId!
				);
			if (!clientSiteOrganizationLocations) {
				toast.error("Failed to get locations");
				return;
			}
			if (
				clientSiteReportInfo.coreTestResultId &&
				clientSiteReportInfo.coreScore != null
			) {
				clientSiteGetCoreTestForReport = await ReportAPI.getCoreTestForReport({
					reportId,
					coreTestResultId: clientSiteReportInfo.coreTestResultId,
				});
				if (!clientSiteGetCoreTestForReport) {
					toast.error("Failed to get core test for report");
					return;
				}
				clientSiteClientCoreErrorEntries =
					await CoreAPI.getClientCoreErrorReportEntries({
						coreTestResultId: clientSiteReportInfo.coreTestResultId,
					});
				if (!clientSiteClientCoreErrorEntries) {
					toast.error("Failed to get core test entries");
					return;
				}
			}
			if (
				clientSiteReportInfo.careTestResultId &&
				clientSiteReportInfo.careScore != null
			) {
				clientSiteGetCareTestForReport = await ReportAPI.getCareTestForReport({
					reportId,
					careTestResultId: clientSiteReportInfo.careTestResultId,
				});
				if (!clientSiteGetCareTestForReport) {
					toast.error("Failed to get Vitals test for report");
					return;
				}
			}

			let coreMapImagePath = "";
			if (
				clientSiteReportInfo.coreTestResultId &&
				clientSiteReportInfo.coreTestResultId > 0
			) {
				// Load map image for CORE
				coreMapImagePath = await CoreAPI.getGoogleMap({
					coreTestResultId: clientSiteReportInfo.coreTestResultId,
					routeColor: Colours.tertiary,
					errorColor: Colours.magenta,
					hazardousErrorColor: Colours.primary,
				});
			}

			const tripInspectionClient: TripInspectionClient =
				this.props.api.tripInspectionClient;
			const assessmentClient: AssessmentClient =
				this.props.api.assessmentClient;
			const userClient: ImpiricaUserClient = this.props.api.impiricaUserClient;
			const datasetsClient: DatasetsClient = this.props.api.datasetsClient;

			let assessmentResult = await assessmentClient.getAssessmentResultForAdmin(
				reportId
			);
			const neurapulseRecommendation = Utils.mapNeurapulseRecommendationType(
				assessmentResult.neurapulseRecommendation
			);
			const neurapulseDateCompleted = assessmentResult.neurapulseCompleted;

			let tripInspection = assessmentResult.tripInspection;
			let taskTypes = tripInspection
				? await tripInspectionClient.getTripTasks(
						tripInspection.vehicleSubTypeId
				  )
				: undefined;
			let observableTypes =
				await tripInspectionClient.getAllTripObservationTypes();

			let tripInspectionUser = tripInspection
				? await userClient.getImpiricaUserForAdmin(tripInspection.createdBy)
				: undefined;

			const datasetPurposeType: DatasetPurposeType =
				await datasetsClient.getDatasetPurpose(datasetId);

			const dataset: DatasetDTO = await DatasetAPI.getOrganizationDataset(
				clientSiteReportInfo.organizationId!,
				datasetId
			);

			// react-pdf + templates load on demand (separate chunk)
			const { pdf, ReportTemplates } = await import("src/admin/report/pdfRuntime");
			const reportTemplate = await pdf(
				<ReportTemplates
					report={clientSiteReportInfo}
					reportSetting={clientSiteReportSettingsInfo}
					locations={clientSiteOrganizationLocations}
					neurapulseRecommendation={neurapulseRecommendation}
					neurapulseDateCompleted={neurapulseDateCompleted}
					coreTestResult={clientSiteGetCoreTestForReport}
					careTestResult={clientSiteGetCareTestForReport}
					coreErrorEntries={clientSiteClientCoreErrorEntries}
					productType={productId}
					mapImagePath={coreMapImagePath}
					picklistContext={this.context}
					tripInspection={tripInspection}
					tripTasks={taskTypes}
					observableTypes={observableTypes}
					tripInspectionUser={tripInspectionUser}
					watermark={datasetPurposeType.watermark}
					datasetPurposeTypeName={datasetPurposeType.name}
					showEmployeeId={dataset.showClientParticipantId}
				/>
			).toBlob();

			const reportAdminTemplate = await pdf(
				<ReportTemplates
					report={clientSiteReportInfo}
					reportSetting={clientSiteReportSettingsInfo}
					locations={clientSiteOrganizationLocations}
					neurapulseRecommendation={neurapulseRecommendation}
					neurapulseDateCompleted={neurapulseDateCompleted}
					coreTestResult={clientSiteGetCoreTestForReport}
					careTestResult={clientSiteGetCareTestForReport}
					coreErrorEntries={clientSiteClientCoreErrorEntries}
					redactInfo={true}
					productType={productId}
					mapImagePath={coreMapImagePath}
					picklistContext={this.context}
					tripInspection={tripInspection}
					tripTasks={taskTypes}
					observableTypes={observableTypes}
					tripInspectionUser={tripInspectionUser}
					watermark={datasetPurposeType.watermark}
					datasetPurposeTypeName={datasetPurposeType.name}
					showEmployeeId={dataset.showClientParticipantId}
				/>
			).toBlob();

			const exceleRATEReport = new File([reportTemplate], "exceleRATEReport");
			const exceleRATEAdminReport = new File(
				[reportAdminTemplate],
				"exceleRATEAdminReport"
			);
			await ReportAPI.saveReportV2Data({ clientId, reportId }, [
				exceleRATEReport,
				exceleRATEAdminReport,
			]);

			ReportAPI.generateV2ReportPDF({
				reportId,
			});
			reportFetched = true;
		}
		return reportFetched;
	};

	private fetchV1ReportData = async () => {
		// V1 (HTML-to-PDF) report generation was retired; the templates under
		// templates/v1 are kept only for on-screen rendering elsewhere.
		notifyV1ReportsNotSupported();
	};

	// TODO: it makes no sense to display Now() as DateScored or DateCompleted if they don't exist.
	private getColumns = () => {
		return [
			{
				Header: <Tooltip title="Web Id">Web Id</Tooltip>,
				accessor: "webId",
				filterable: true,
			},
			{
				Header: (
					<Tooltip title="In-Office Completed">In-Office Completed</Tooltip>
				),
				accessor: "vitalsDateCompleted",
				Cell: setDateColumnCellValue,
				Filter: ({ onChange }: { onChange: any }) => (
					<DateFilter
						accessor="vitalsDateCompleted"
						date={this.state.vitalsDateCompleted}
						onChange={onChange}
						onDateChange={this.onDateChange}
					/>
				),
				filterMethod: dateFilterMethod("vitalsDateCompleted"),
			},
			{
				Header: <Tooltip title="On-road Completed">On-road Completed</Tooltip>,
				accessor: "coreDateCompleted",
				Cell: setDateColumnCellValue,
				Filter: ({ onChange }: { onChange: any }) => (
					<DateFilter
						accessor="coreDateCompleted"
						date={this.state.coreDateCompleted}
						onChange={onChange}
						onDateChange={this.onDateChange}
					/>
				),
				filterMethod: dateFilterMethod("coreDateCompleted"),
			},
			{
				Header: <Tooltip title="Created by">Created by</Tooltip>,
				accessor: "evaluator",
			},
			{
				Header: <Tooltip title="Client Code">Participant Code</Tooltip>,
				accessor: "clientCode",
			},
			{
				Header: <Tooltip title="Test Type">Test Type</Tooltip>,
				accessor: "testType",
			},
			{
				Header: <Tooltip title="Dataset">Dataset</Tooltip>,
				accessor: "datasetName",
			},
			{
				Header: <Tooltip title="Organization">Organization</Tooltip>,
				accessor: "organizationName",
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				accessor: "Actions",
				Cell: (rowInfo: any) => (
					<>
						<GenerateExceleRATEPdf
							fetchReportData={() =>
								this.fetchReportData(
									rowInfo.original.id,
									rowInfo.original.reportId,
									rowInfo.original.dataLocked,
									rowInfo.original.datasetProductId,
									rowInfo.original.datasetId
								)
							}
						/>
					</>
				),
				Filter: () => (
					<Button
						className="btn-outline-primary"
						onClick={() =>
							this.setState({
								filtered: [],
								coreDateCompleted: null,
								vitalsDateCompleted: null,
							})
						}
					>
						Clear
					</Button>
				),
			},
		];
	};
}
export default ReportList;
